import React from 'react';
import { GeometricProperties, PartType } from '../../types/studio';

interface PartPreviewProps {
  type: PartType;
  geometry: GeometricProperties;
}

const PartPreview: React.FC<PartPreviewProps> = ({ type, geometry }) => {
  const { h, w, t, l = 0, hw = 0 } = geometry;
  const d = geometry.d || 0; // Step Height
  const D = geometry.D || 0; // Top Flange Width

  // Canvas settings
  const canvasSize = 400;
  const padding = 80; // More padding for labels
  const maxDim = Math.max(h, w) || 1;
  
  // Scale factor to fit the shape in the box
  const scale = (canvasSize - padding * 2) / maxDim;

  // Center points
  const cx = canvasSize / 2;
  const cy = canvasSize / 2;

  // Scaled dimensions
  const sh = h * scale;
  const sw = w * scale;
  const st = t * scale * 2; // Exaggerate thickness
  const sl = (l || 0) * scale;
  const sd = d * scale;
  const sD = D * scale;

  // Common vars
  const startX = cx - sw / 2;
  const startY = cy - sh / 2;

  // Helper to generate path strings
  const generatePath = () => {
    if (type === PartType.PIPE) return "";
    
    if (type === PartType.ANGLE || type === PartType.BRACKET) {
        // L Shape
        const p1 = `${startX},${startY}`; // Top Left Tip
        const p2 = `${startX},${startY + sh}`; // Bottom Left Corner (Outer)
        const p3 = `${startX + sw},${startY + sh}`; // Bottom Right Tip
        const p4 = `${startX + sw},${startY + sh - st}`; // Bottom Right Inner
        const p5 = `${startX + st},${startY + sh - st}`; // Inner Corner
        const p6 = `${startX + st},${startY}`; // Top Left Inner
        return `M ${p1} L ${p2} L ${p3} L ${p4} L ${p5} L ${p6} Z`;
    }

    if (type === PartType.STEP_BEAM) {
        // Closed Step Beam
        const ptBL = `${startX},${startY + sh}`;
        const ptTL = `${startX},${startY}`;
        const ptTS = `${startX + sD},${startY}`;
        const ptSD = `${startX + sD},${startY + sd}`;
        const ptSO = `${startX + sw},${startY + sd}`;
        const ptBR = `${startX + sw},${startY + sh}`;

        // Outer Path
        const outer = `M ${ptBL} L ${ptTL} L ${ptTS} L ${ptSD} L ${ptSO} L ${ptBR} Z`;
        
        // Inner Path
        const iBL = `${startX + st},${startY + sh - st}`;
        const iTL = `${startX + st},${startY + st}`;
        const iTS = `${startX + sD - st},${startY + st}`;
        const iSD = `${startX + sD - st},${startY + sd + st}`;
        const iSO = `${startX + sw - st},${startY + sd + st}`;
        const iBR = `${startX + sw - st},${startY + sh - st}`;

        const inner = `M ${iBL} L ${iTL} L ${iTS} L ${iSD} L ${iSO} L ${iBR} Z`;

        return `${outer} ${inner}`;
    }

    if (type === PartType.CLOSED_TUBE) {
      const outer = `M ${startX},${startY} h ${sw} v ${sh} h -${sw} Z`;
      const inner = `M ${startX + st},${startY + st} v ${sh - 2*st} h ${sw - 2*st} v -${sh - 2*st} Z`;
      return `${outer} ${inner}`;
    }
    
    if (type === PartType.C_CHANNEL || type === PartType.C_CHANNEL_LIPS || type === PartType.C_CHANNEL_LIPS_RIB) {
      const xTR = startX + sw;
      const yTR = startY + sl;
      let dPath = "";
      if (type === PartType.C_CHANNEL) {
          dPath = `M ${startX + sw},${startY} h -${sw} `;
      } else {
          dPath = `M ${xTR},${yTR} v -${sl} h -${sw} `;
      }
      
      if (type === PartType.C_CHANNEL_LIPS_RIB) {
          const ribDepth = 4; const ribHeight = sh / 3;
          dPath += ` v ${sh/2 - ribHeight/2} h ${ribDepth} v ${ribHeight} h -${ribDepth} v ${sh/2 - ribHeight/2} `; 
      } else {
          dPath += ` v ${sh} `;
      }

      dPath += ` h ${sw} `;  
      
      if (type !== PartType.C_CHANNEL) {
        dPath += ` v -${sl} h -${st} v ${sl - st} `;
      } else {
        dPath += ` v -${st} `;
      }

      dPath += ` h -${sw - 2*st} v -${sh - 2*st} h ${sw - 2*st} `;
      
      if (type !== PartType.C_CHANNEL) {
          dPath += ` v ${sl - st} h ${st} `;
      } else {
          dPath += ` v ${st} `;
      }
      return dPath;
    }
    
    return `M ${startX},${startY} h ${sw} v ${sh} h -${sw} Z`;
  };

  const renderConnectionPart = () => {
    if (type === PartType.BOLT || type === PartType.PIN) {
        // Bolt/Pin Visualization (Side View)
        // h = Diameter, assume length is proportional for icon
        const dia = sh; 
        const len = 120; // Fixed visual length
        const headH = dia * 0.6;
        const headW = dia * 1.5;
        
        // Center
        const bx = cx - len/2; 
        const by = cy;

        return (
            <g>
                {/* CL Line */}
                <line x1={bx - 20} y1={cy} x2={bx + len + 40} y2={cy} stroke="#397D77" strokeDasharray="5,3" />
                <text x={bx - 30} y={cy} fill="#34495E" dominantBaseline="middle" textAnchor="end" className="text-lg font-bold">CL</text>
                <text x={bx + len + 50} y={cy} fill="#34495E" dominantBaseline="middle" className="text-lg font-bold">CL</text>
                
                {/* Head */}
                <rect x={bx} y={cy - headW/2} width={headH} height={headW} fill="none" stroke="#666" strokeWidth="2" />
                
                {/* Shank */}
                <rect x={bx + headH} y={cy - dia/2} width={len} height={dia} fill="none" stroke="#666" strokeWidth="2" />
                
                {/* Threads (Stylized lines) */}
                {[1,2,3,4,5,6,7].map(i => (
                    <line key={i} x1={bx + headH + 10 + i*10} y1={cy - dia/2} x2={bx + headH + 10 + i*10} y2={cy + dia/2} stroke="#AAA" />
                ))}

                {/* Diameter Annotation */}
                <line x1={bx + headH + len/2} y1={cy - dia/2} x2={bx + headH + len/2} y2={cy - dia/2 - 30} stroke="#397D77" />
                <line x1={bx + headH + len/2} y1={cy + dia/2} x2={bx + headH + len/2} y2={cy - dia/2 - 30} stroke="#397D77" />
                
                <text x={bx + headH + len/2} y={cy - dia/2 - 40} fill="#397D77" textAnchor="middle" className="text-sm font-bold">d</text>
                <text x={bx + headH + len/2 + 10} y={cy - dia/2 - 40} fill="#397D77" className="text-xs">/2</text>
                
                {/* D/2 Arrow */}
                <line x1={bx + headH + len/2} y1={cy - dia/2 - 20} x2={bx + headH + len/2} y2={cy} stroke="#397D77" markerStart="url(#arrow)" />
                
                {/* Note */}
                <text x={cx} y={cy + 80} fill="#34495E" textAnchor="middle" className="text-xs font-bold">Note: CL Represents {type} Centerline</text>
            </g>
        );
    }
    
    if (type === PartType.TAB) {
        // Tab Visualization (Simple shape)
        // ttab (width) => w input
        const ttab = sw; 
        const tabH = 100;
        // Draw a generic tab shape
        return (
            <g transform={`translate(${cx-50}, ${cy-50})`}>
                 <path d={`M 0,0 L ${ttab},20 L ${ttab},80 L 20,100 L 0,80 Z`} fill="none" stroke="#666" strokeWidth="2" />
                 
                 {/* Annotation */}
                 <line x1={0} y1={-10} x2={ttab} y2={10} stroke="#397D77" markerEnd="url(#arrow)" markerStart="url(#arrow)" />
                 <text x={0} y={-20} fill="#397D77" className="text-lg font-bold">t</text>
                 <text x={10} y={-15} fill="#397D77" className="text-xs font-bold">tab</text>
            </g>
        );
    }
    return null;
  };

  if (type === PartType.BOLT || type === PartType.PIN || type === PartType.TAB) {
     return (
        <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
          <div className="relative w-full h-full flex items-center justify-center">
            <svg width="100%" height="100%" viewBox={`0 0 ${canvasSize} ${canvasSize}`} className="overflow-visible">
                 {renderConnectionPart()}
                 <defs>
                    <marker id="arrow" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
                        <path d="M 0 0 L 6 3 L 0 6 z" fill="#397D77" />
                    </marker>
                 </defs>
            </svg>
          </div>
        </div>
     );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
      <div className="relative w-full h-full flex items-center justify-center">
        <svg width="100%" height="100%" viewBox={`0 0 ${canvasSize} ${canvasSize}`} className="overflow-visible">
            
            {/* The Shape */}
            {type === PartType.PIPE ? (
                <g>
                    <circle cx={cx} cy={cy} r={sh/2} fill="none" stroke="#666" strokeWidth="2" />
                    <circle cx={cx} cy={cy} r={sh/2 - st} fill="none" stroke="#666" strokeWidth="2" />
                </g>
            ) : (
                <path 
                    d={generatePath()} 
                    fill="none" 
                    stroke="#666" 
                    strokeWidth="2"
                    fillRule="evenodd"
                />
            )}
            
            {/* --- Dimensions Annotations --- */}

            {/* Height / Diameter (Left side) */}
            {type === PartType.PIPE ? (
                 <>
                 <line x1={cx} y1={cy - sh/2 - 20} x2={cx} y2={cy + sh/2 + 20} stroke="#397D77" strokeDasharray="4" />
                 <line x1={cx - sh/2} y1={cy - sh/2 - 15} x2={cx + sh/2} y2={cy - sh/2 - 15} stroke="#397D77" markerEnd="url(#arrow)" markerStart="url(#arrow)" />
                 <text x={cx} y={cy - sh/2 - 25} fill="#397D77" textAnchor="middle" className="text-sm font-bold font-mono">D</text>
                 </>
            ) : (
                <>
                <line x1={startX - 20} y1={startY} x2={startX - 20} y2={startY + sh} stroke="#397D77" markerEnd="url(#arrow)" markerStart="url(#arrow)" />
                <line x1={startX - 25} y1={startY} x2={startX - 15} y2={startY} stroke="#397D77" /> 
                <line x1={startX - 25} y1={startY + sh} x2={startX - 15} y2={startY + sh} stroke="#397D77" /> 
                <text x={startX - 35} y={cy} fill="#397D77" textAnchor="end" dominantBaseline="middle" className="text-sm font-bold font-mono">H</text>
                </>
            )}

             {/* Width (Bottom side) - Hide for Pipe */}
             {type !== PartType.PIPE && (
                 <>
                 <line x1={startX} y1={startY + sh + 20} x2={startX + sw} y2={startY + sh + 20} stroke="#397D77" markerEnd="url(#arrow)" markerStart="url(#arrow)" />
                 <line x1={startX} y1={startY + sh + 25} x2={startX} y2={startY + sh + 15} stroke="#397D77" /> 
                 <line x1={startX + sw} y1={startY + sh + 25} x2={startX + sw} y2={startY + sh + 15} stroke="#397D77" /> 
                 <text x={cx} y={startY + sh + 35} fill="#397D77" textAnchor="middle" dominantBaseline="hanging" className="text-sm font-bold font-mono">
                    {type === PartType.BRACKET ? "B" : "W"}
                 </text>
                 </>
             )}

            {/* Thickness */}
            {type === PartType.PIPE ? (
                 <>
                 <line x1={cx} y1={cy + sh/2} x2={cx} y2={cy + sh/2 - st} stroke="#397D77" />
                 <text x={cx} y={cy + sh/2 + 15} fill="#397D77" textAnchor="middle" className="text-sm font-bold font-mono">t</text>
                 </>
            ) : (
                 <>
                 {/* Thickness Annotation, vary for Bracket */}
                 {type === PartType.BRACKET ? (
                     <>
                        <line x1={startX + sw} y1={startY - 20} x2={startX + sw - st} y2={startY - 20} stroke="#397D77" markerStart="url(#arrow)" />
                        <line x1={startX + sw} y1={startY - 25} x2={startX + sw} y2={startY} stroke="#397D77" />
                        <text x={startX + sw} y={startY - 30} fill="#397D77" textAnchor="middle" className="text-sm font-bold font-mono">T</text>
                        
                        {/* Bolt Location Annotation */}
                        <path d={`M ${cx - 50},${cy - 50} Q ${cx},${cy - 50} ${cx + 10},${cy + sh/2 - 20}`} fill="none" stroke="#666" strokeWidth="1" />
                        <polygon points={`${cx+10},${cy+sh/2-20} ${cx+5},${cy+sh/2-30} ${cx+15},${cy+sh/2-30}`} fill="#666" />
                        <text x={cx - 50} y={cy - 55} fill="#34495E" className="text-xs font-bold">Bolt, Pin, or</text>
                        <text x={cx - 50} y={cy - 45} fill="#34495E" className="text-xs font-bold">Tab Location</text>
                     </>
                 ) : (
                     <>
                     <line x1={cx + sw/2 + 20} y1={cy + sh/2 - st/2} x2={cx + sw/2} y2={cy + sh/2 - st/2} stroke="#397D77" markerStart="url(#arrow)" />
                     <text x={cx + sw/2 + 25} y={cy + sh/2 - st/2} fill="#397D77" dominantBaseline="middle" className="text-sm font-bold font-mono">t</text>
                     </>
                 )}
                 </>
            )}

            <defs>
                <marker id="arrow" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
                    <path d="M 0 0 L 6 3 L 0 6 z" fill="#397D77" />
                </marker>
            </defs>

        </svg>
      </div>
    </div>
  );
};

export default PartPreview;
